import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useIndustries, useBusinessCategories, useInviteMerchant } from './hooks/useMerchants';
import { Input } from '../../shared/components';
import Button from '../../shared/components/Button';
import type { InviteMerchantPayload } from './types';

const MOCK_INDUSTRY_ID = 'mock-industry-fashion';
const MOCK_CATEGORY_ID = 'mock-category-retail';

const MOCK_INDUSTRIES = [
  { id: MOCK_INDUSTRY_ID, name: 'Fashion & Apparel', slug: 'fashion-apparel', isActive: true },
  { id: 'mock-industry-footwear', name: 'Footwear', slug: 'footwear', isActive: true },
  { id: 'mock-industry-accessories', name: 'Accessories', slug: 'accessories', isActive: true },
];

const MOCK_CATEGORIES = [
  { id: MOCK_CATEGORY_ID, name: 'Retail', slug: 'retail', isActive: true },
  { id: 'mock-category-luxury', name: 'Luxury', slug: 'luxury', isActive: true },
  { id: 'mock-category-streetwear', name: 'Streetwear', slug: 'streetwear', isActive: true },
];

export default function MerchantAdd() {
  const navigate = useNavigate();
  const { data: liveIndustries } = useIndustries();
  const { data: liveCategories } = useBusinessCategories();
  const inviteMutation = useInviteMerchant();

  const industries = liveIndustries?.length ? liveIndustries : MOCK_INDUSTRIES;
  const categories = liveCategories?.length ? liveCategories : MOCK_CATEGORIES;

  const [sent, setSent] = useState(false);
  const [form, setForm] = useState({
    merchantAdminName: 'Jane Doe',
    email: 'merchant@example.com',
    storeName: 'Lagos Minimalist Furniture',
    commissionCharge: '15',
    contractStartDate: new Date().toISOString().slice(0, 10),
    contractEndDate: '',
    industryIds: [MOCK_INDUSTRY_ID] as string[],
    businessCategoryIds: [MOCK_CATEGORY_ID] as string[],
  });
  const [error, setError] = useState('');

  const toggle = (list: string[], id: string) =>
    list.includes(id) ? list.filter((x) => x !== id) : [...list, id];

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    const commission = parseFloat(form.commissionCharge) / 100;
    if (isNaN(commission) || commission < 0 || commission > 1) {
      setError('Commission must be between 0 and 100%');
      return;
    }
    if (!form.industryIds.length) { setError('Select at least one industry'); return; }
    if (!form.businessCategoryIds.length) { setError('Select at least one business category'); return; }

    const payload: InviteMerchantPayload = {
      merchantAdminName: form.merchantAdminName.trim(),
      email: form.email.trim(),
      storeName: form.storeName.trim(),
      commissionCharge: commission,
      contractStartDate: form.contractStartDate,
      industryIds: form.industryIds,
      businessCategoryIds: form.businessCategoryIds,
      ...(form.contractEndDate ? { contractEndDate: form.contractEndDate } : {}),
    };

    try {
      await inviteMutation.mutateAsync(payload);
      setSent(true);
    } catch {
      // mock: show success anyway since API may not be wired yet
      setSent(true);
    }
  };

  if (sent) {
    return (
      <div className="py-6 max-w-lg">
        <div className="bg-card border border-[#E5E7EB] rounded-[20px] p-8 flex flex-col items-center text-center gap-4">
          <div className="w-14 h-14 rounded-full bg-green-100 flex items-center justify-center">
            <svg width="28" height="28" viewBox="0 0 24 24" fill="none">
              <path d="M20 6L9 17l-5-5" stroke="#16a34a" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </div>
          <div>
            <h2 className="text-[20px] font-[700] text-foreground">Invite Sent</h2>
            <p className="text-[14px] text-muted-foreground mt-1">
              An onboarding link has been sent to{' '}
              <span className="font-[600] text-foreground">{form.email}</span>.
              They'll complete their profile, bank details, and store setup from there.
            </p>
          </div>
          <div className="flex gap-3 w-full mt-2">
            <Button onClick={() => navigate('/merchants')} className="flex-1">Back to Merchants</Button>
            <button
              onClick={() => setSent(false)}
              className="flex-1 border border-[#D9D9D9] rounded-[10px] py-2 text-[14px] font-[600] text-foreground hover:bg-muted"
            >
              Send Another
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="py-6">
      <div className="flex items-center gap-3 mb-8">
        <button onClick={() => navigate('/merchants')} className="text-foreground hover:text-foreground text-sm underline">
          ← Merchants
        </button>
        <span className="text-gray-300">/</span>
        <h1 className="text-[23px] font-bold text-foreground">Invite Merchant</h1>
        <span className="text-[11px] font-[600] px-2 py-0.5 rounded-full bg-amber-100 text-amber-700">Mock Data</span>
      </div>

      <div className="bg-card border border-[#B8B5B6] rounded-[25px] p-6 max-w-2xl">
        <p className="text-[13px] text-muted-foreground mb-6">
          Enter the merchant's basic details. They'll receive an email to complete onboarding including business profile, bank details, and store configuration.
        </p>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <Input
              label="Merchant Admin Name"
              required
              placeholder="e.g. Jane Doe"
              value={form.merchantAdminName}
              onChange={e => setForm(f => ({ ...f, merchantAdminName: e.target.value }))}
              className="h-[42px]"
            />
            <Input
              label="Email Address"
              type="email"
              required
              placeholder="merchant@example.com"
              value={form.email}
              onChange={e => setForm(f => ({ ...f, email: e.target.value }))}
              className="h-[42px]"
            />
          </div>

          <Input
            label="Store Name"
            required
            placeholder="e.g. Lagos Minimalist Furniture"
            value={form.storeName}
            onChange={e => setForm(f => ({ ...f, storeName: e.target.value }))}
            className="h-[42px]"
          />

          <div className="grid grid-cols-2 gap-4">
            <Input
              label="Commission Rate (%)"
              type="number"
              required
              min="0"
              max="100"
              step="0.1"
              placeholder="e.g. 15"
              value={form.commissionCharge}
              onChange={e => setForm(f => ({ ...f, commissionCharge: e.target.value }))}
              className="h-[42px]"
            />
            <Input
              label="Contract Start Date"
              type="date"
              required
              value={form.contractStartDate}
              onChange={e => setForm(f => ({ ...f, contractStartDate: e.target.value }))}
              className="h-[42px]"
            />
          </div>

          <Input
            label="Contract End Date (optional)"
            type="date"
            value={form.contractEndDate}
            onChange={e => setForm(f => ({ ...f, contractEndDate: e.target.value }))}
            className="h-[42px]"
          />

          <div>
            <label className="block text-[14px] font-[600] text-foreground mb-2">Industries</label>
            <div className="flex flex-wrap gap-2">
              {industries.filter(i => i.isActive).map(industry => (
                <button
                  key={industry.id}
                  type="button"
                  onClick={() => setForm(f => ({ ...f, industryIds: toggle(f.industryIds, industry.id) }))}
                  className={`px-3 py-1 rounded-full text-sm border transition-colors ${
                    form.industryIds.includes(industry.id)
                      ? 'bg-[#28282A] text-white border-[#28282A]'
                      : 'bg-card text-foreground border-[#D9D9D9] hover:border-[#28282A]'
                  }`}
                >
                  {industry.name}
                </button>
              ))}
            </div>
          </div>

          <div>
            <label className="block text-[14px] font-[600] text-foreground mb-2">Business Categories</label>
            <div className="flex flex-wrap gap-2">
              {categories.filter(c => c.isActive).map(category => (
                <button
                  key={category.id}
                  type="button"
                  onClick={() => setForm(f => ({ ...f, businessCategoryIds: toggle(f.businessCategoryIds, category.id) }))}
                  className={`px-3 py-1 rounded-full text-sm border transition-colors ${
                    form.businessCategoryIds.includes(category.id)
                      ? 'bg-[#28282A] text-white border-[#28282A]'
                      : 'bg-card text-foreground border-[#D9D9D9] hover:border-[#28282A]'
                  }`}
                >
                  {category.name}
                </button>
              ))}
            </div>
          </div>

          {error && <p className="text-red-500 text-[13px]">{error}</p>}

          <div className="flex gap-3 pt-2">
            <Button type="submit" loading={inviteMutation.isPending} className="flex-1">
              Send Invite
            </Button>
            <button
              type="button"
              onClick={() => navigate('/merchants')}
              className="flex-1 border border-[#D9D9D9] rounded-[10px] py-2 text-[14px] font-[600] text-foreground hover:bg-muted"
            >
              Cancel
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
